import React, { useState } from 'react';
import axios from 'axios';
import { SIGNER_ROLES, errorMessage } from '../../utils/staffDocuments';
import { Sheet } from '../ui';
import WhoSigns from './WhoSigns';
import { SectionsFields } from './DocumentEditor';
import './staffDocuments.css';

const DEFAULT_DECLARATION = 'I have read and understood this document.';
const blankSection = () => ({ heading: '', items: '' });
const lines = text => text.split('\n').map(l => l.trim()).filter(Boolean);

const TITLES = { write: 'Write a document', template: 'Use our template', upload: 'Upload a PDF' };

// Admin, with the staffDocuments feature on: add a written document, a copy of
// the Run The Club code of conduct, or an uploaded PDF. The form opens in a
// pop-up. onCreated gets the new document.
export default function AddDocument({ online, onCreated }) {
  const [mode, setMode] = useState(null);
  return (
    <div className="coc-add">
      <h3>Add a document</h3>
      <p className="bk-small sd-add__hint">Write your own, start from the Run The Club code of conduct, or upload a PDF you already have.</p>
      <div className="bk-actions bk-actions--3">
        <button type="button" className="bk-btn" onClick={() => setMode('write')}>Write one</button>
        <button type="button" className="bk-btn" onClick={() => setMode('template')}>Use our template</button>
        <button type="button" className="bk-btn" onClick={() => setMode('upload')}>Upload a PDF</button>
      </div>
      {mode && <AddSheet key={mode} mode={mode} online={online} onClose={() => setMode(null)} onCreated={onCreated} />}
    </div>
  );
}

function AddSheet({ mode, online, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [sections, setSections] = useState([blankSection()]);
  const [declaration, setDeclaration] = useState(DEFAULT_DECLARATION);
  const [file, setFile] = useState(null);
  const [signerRoles, setSignerRoles] = useState(SIGNER_ROLES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const dirty = !!(title.trim() || intro.trim() || file || sections.some(s => s.heading.trim() || s.items.trim())
    || declaration !== DEFAULT_DECLARATION || signerRoles.length !== SIGNER_ROLES.length);
  const ready = mode === 'template' || (title.trim() && (mode === 'upload' ? file : sections.some(s => s.heading.trim() && lines(s.items).length)));

  const submit = async () => {
    if (!ready || !online) return;
    setBusy(true);
    setError(null);
    try {
      let res;
      if (mode === 'template') {
        res = await axios.post('/api/staff-documents', { template: 'codeOfConduct', signerRoles });
      } else if (mode === 'upload') {
        const form = new FormData();
        form.append('file', file);
        form.append('title', title.trim());
        form.append('declaration', declaration.trim());
        form.append('signerRoles', JSON.stringify(signerRoles));
        res = await axios.post('/api/staff-documents/upload', form);
      } else {
        res = await axios.post('/api/staff-documents', {
          title: title.trim(),
          intro: intro.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean),
          sections: sections.map(s => ({ heading: s.heading.trim(), items: lines(s.items) })).filter(s => s.heading && s.items.length),
          declaration: declaration.trim(),
          signerRoles,
        });
      }
      onCreated(res.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't add the document. Try again."));
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={TITLES[mode]}
      wide
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={busy || !online || !ready}>{busy ? 'Adding…' : 'Add'}</button>
          <button type="button" className="bk-btn" onClick={requestClose} disabled={busy}>Cancel</button>
        </div>
      )}
    >
      <div className="sd-form">
        {mode === 'template' && <p className="bk-small">You get your own copy of the Run The Club code of conduct, which you can edit afterwards.</p>}
        {mode !== 'template' && (
          <label className="bk-label" htmlFor="add-title">
            Title
            <input id="add-title" className="bk-input" value={title} onChange={e => setTitle(e.target.value)} />
          </label>
        )}
        {mode === 'upload' && (
          <label className="bk-label" htmlFor="add-file">
            PDF (up to 15 MB)
            <input id="add-file" type="file" accept=".pdf,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} />
          </label>
        )}
        {mode === 'write' && (
          <SectionsFields idPrefix="add" intro={intro} setIntro={setIntro} sections={sections} setSections={setSections} />
        )}
        {mode !== 'template' && (
          <label className="bk-label" htmlFor="add-declaration">
            What staff agree to when they sign
            <textarea id="add-declaration" className="bk-input" rows={2} value={declaration} onChange={e => setDeclaration(e.target.value)} />
          </label>
        )}
        <WhoSigns value={signerRoles} onChange={setSignerRoles} disabled={busy} />
        {!online && <p className="bk-small">Adding a document needs signal.</p>}
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}
